import { create } from 'zustand';
import type { StandardLoanState } from './standardLoanState';
import { createSetupSlice, initialSetupState } from './slices/setupSlice';
import { createBorrowerSlice, initialBorrowerState } from './slices/borrowerSlice';
import { createLoanSlice, initialLoanState } from './slices/loanSlice';
import { createPropertySlice, initialPropertyState } from './slices/propertySlice';
import { getSetupFromLoan } from '../components/Steps/components/SetupAndBorrower/Setup/functions/getSetupFromLoan';
import { getBorrowerFromLoan } from '../components/Steps/components/SetupAndBorrower/Borrower/functions/getBorrowerFromLoan';
import { getLoanFromLoan } from '../components/Steps/components/Loan/LoanInfo/functions/getLoanFromLoan';
import { getPropertiesFromLoan } from '../components/Steps/components/Property/functions/getPropertiesFromLoan';

/**
 * The modal's form data, shared by the step list (progress) and each step's content (editing). Lives outside any
 * component, so every step's progress is known whether or not that step is open.
 *
 * Each step has a slice in ./slices (its form + update action, its context + setter, and its initial values). To add a
 * step, add its slice here, to StandardLoanState, to loadLoan() and to reset().
 */
export const useStandardLoanStore = create<StandardLoanState>()((set, get, store) => ({
    ...createSetupSlice(set, get, store),
    ...createBorrowerSlice(set, get, store),
    ...createLoanSlice(set, get, store),
    ...createPropertySlice(set, get, store),

    loadedLoan: undefined,

    // One set(), so the steps never see a half-loaded loan. Values go straight in, not through the slices' update
    // actions, which would treat them as edits (e.g. recalculating the Maturity Date the loan already has)
    loadLoan: (loadedLoan, source) => {
        const loanTypeId = loadedLoan.loanTypeId ?? source.loanTypeId;
        set({
            loadedLoan,
            ...getSetupFromLoan(loadedLoan, loanTypeId),
            maturityDateAutoCalculated: false,
            ...getBorrowerFromLoan(loadedLoan, !!source.bClientId),
            ...getLoanFromLoan(loadedLoan, loanTypeId),
            properties: getPropertiesFromLoan(loadedLoan),
        });
    },

    reset: () =>
        set({
            ...initialSetupState,
            ...initialBorrowerState,
            ...initialLoanState,
            ...initialPropertyState,
            loadedLoan: undefined,
        }),
}));
